/** @format */
import React, { useEffect, useCallback, createRef } from 'react';
import { Button, Grid } from '@mui/material';
import useCustomHook from '../hooks';
import {
  PageContainer,
  ButtonLoading,
  InputV2,
  AutocompleteV,
  DatePickerPod,
  RadioGroupPod,
  TargetUser,
  ConfirmDialog,
  Icons,
  PickList,
} from 'components';
import { DatePicker } from 'components/LegacyPickers';
import { useStyles } from './Style';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { enifyDateTime, constBreadCrumbsTitle } from 'utility';
import jMoment from 'moment-jalaali';
import { useTargetUser, useGuildsList } from 'hooks';
import IssueInvoiceServices from './services/IssueInvoiceServices';
import { Preview, CustomTable } from './components';
import createToast from 'components/Toast/Toast';
import { useCurrencyUnit } from 'hooks';

const ImprestIssueInvoice = () => {
  const defaultValues = {
    billNumber: '', // [String]
    deadline: jMoment(new Date()).add(10, 'days'), // String
    guildCode: null, // محل واریز درآمد
    productId: [],
    productName: [],
    price: [],
    quantity: [],
    discount: [],
    productDescription: [],
    voucherHashs: [],
    preferredTaxRate: 9,
    postVoucherEnabled: 'true',
    verificationNeeded: 'false',
  };

  // -- STATE --//
  const currencyText = useCurrencyUnit(false);

  const inputRef = createRef();
  // -- HOOKS --//
  const {
    selectedProducts,
    setSelectedProducts,
    previewLoading,
    setPreviewLoading,
    previewData,
    setPreviewData,
    isOpenPreview,
    setIsOpenPreview,
    voucherHash,
    setVoucherHash,
    voucherHashs,
    setVoucherHashs,
    fileCounter,
    setFileCounter,
    loading,
    setLoading,
  } = useCustomHook();
  const { getGuildsList, guildListLoading, businessGuildsList } = useGuildsList();
  const targetUser = useTargetUser();
  const methods = useForm({ defaultValues });
  const classes = useStyles();
  const navigate = useNavigate();
  const params = useParams();
  useEffect(() => {
    getGuildsList();
  }, []);

  useEffect(() => {
    methods.register('guildCode');
    methods.register('deadline');
    methods.register('voucher');
    methods.register('description');
    methods.register('preferredTaxRate');
    return () => {
      methods.unregister('guildCode');
      methods.unregister('deadline');
      methods.unregister('voucher');
      methods.unregister('description');
      methods.unregister('preferredTaxRate');
    };
  }, [methods.register, methods.unregister]);

  const getInvoiceBody = () => {
    const data = methods.getValues();

    const invoiceBody = {
      //businessId: params.businessId,
      userId: targetUser?.userId || null,
      billNumber: data.billNumber || null, // String
      guildCode: data.guildCode?.code ?? null, // String
      deadline: data.deadline ? enifyDateTime(data.deadline?.format('jYYYY/jMM/jDD')) : null, // String
      productId: selectedProducts?.map?.((p) => p.id), // [int]
      price: data?.product?.map?.((p) => p.price), // [Positive Number]
      quantity: data?.product?.map?.((p) => Number(p.quantity)), // [int]
      discount: data?.product?.map?.((p) => (p.discount === '' ? null : Number(p.discount))),
      productDescription: data?.product?.map?.((p, k) =>
        getDescriptionText(p, selectedProducts[k]),
      ), // [string]
      voucherHash: voucherHashs, // [string]
      preferredTaxRate: data.preferredTaxRate === '' ? null : String(data.preferredTaxRate),
      description: data?.description || '',
      verificationNeeded: data.verificationNeeded,
      postVoucherEnabled: data.postVoucherEnabled,
    };
    return invoiceBody;
  };

  // -- API REQUESTS --//

  const checkSubmit = (submiter) => {
    submiter();
  };

  async function issueInvoice() {
    checkSubmit(async () => {
      const invoiceBody = getInvoiceBody();
      setLoading(true);
      try {
        await IssueInvoiceServices.issueInvoice(invoiceBody, params.businessId);
        navigate('/Imprest');
      } catch (error) {
      } finally {
        setLoading(false);
        setIsOpenPreview(false);
      }
    });
  }

  async function previewIssueInvoice() {
    checkSubmit(async () => {
      const invoiceBody = getInvoiceBody();
      setPreviewLoading(true);
      try {
        const res = await IssueInvoiceServices.previewIssueInvoice(invoiceBody, params.businessId);
        setPreviewData(res?.data?.result);
        setIsOpenPreview(true);
      } catch (error) {
      } finally {
        setPreviewLoading(false);
      }
    });
  }

  const onAddVoucher = useCallback(
    (valueFromFile) => {
      const normalizer = (text = '') =>
        text
          .split(',')
          .map((v) => v.trim())
          .filter((v) => !!v);

      setVoucherHashs((state) => [...state, ...normalizer(valueFromFile || voucherHash)]);
      setVoucherHash('');
    },
    [voucherHash, setVoucherHash],
  );

  const onDeleteVoucher = useCallback(
    (v) => {
      setVoucherHashs((state) => state.filter((vo) => vo !== v));
    },
    [setVoucherHashs],
  );

  const onSelectFile = (e) => {
    if (window.File && window.FileReader && window.FileList && window.Blob) {
      const file = e.target.files[0];
      if (!file) {
        createToast('خطا', 'ابتدا یک فایل .txt انتخاب کنید', 'danger');
      }
      setFileCounter((state) => state + 1);
      const reader = new FileReader();
      const textFile = /text.*/;

      if (file?.type?.match(textFile)) {
        reader.onload = function (event) {
          try {
            const lines = event.target.result?.split?.('\n').join(',');
            onAddVoucher(lines);
          } catch (error) {}
        };
      } else {
        createToast('خطا', 'فایل انتخاب شده معتبر نمی‌باشد', 'danger');
      }
      reader.readAsText(file);
    } else {
      createToast('خطا', 'مرورگر شما از این ویژگی پشتیبانی نمی‌کند', 'danger');
    }
  };

  const getDescriptionText = (product, idAndName) => {
    if (idAndName.id == 0)
      return `${idAndName.name ?? ''}${idAndName.name && product.description ? ' - ' : '_'}${
        product.description ?? ''
      }`;

    return `${product.description ?? '_'}`;
  };
  const hintText =
    'با استفاده از این ابزار می‌توانید فاکتور با مبلغ دلخواه و برای محصول دلخواه صادر کنید. (برای موارد تست درگاه، فاکتورهای خاص و یا دریافت مبالغ دلخواه از مشتریان کاربردی است.)';

  return (
    <PageContainer
      description={hintText}
      title="صدور فاکتور از طرف"
      breadcrumbs={constBreadCrumbsTitle.IMPREST_ISSUEINVOICE}
      meta={
        <Button
          color="primary"
          variant="outlined"
          endIcon={<Icons icon="CaretLeft" color="#2b3990" size="25" />}
          onClick={() => navigate('/Imprest')}
        >
          بازگشت به لیست تنخواه گردان
        </Button>
      }
    >
      <TargetUser />
      <FormProvider {...methods}>
        <form noValidate autoComplete="off" onSubmit={methods.handleSubmit(previewIssueInvoice)}>
          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={2}
            item
            className={classes.root}
            xs={12}
          >
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <InputV2
                fullWidth
                name="billNumber"
                label="شماره قبض"
                errors={methods.formState.errors}
                register={methods.register}
                control={methods.control}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <DatePickerPod label="تاریخ سررسید" showAsterisk>
                <DatePicker
                  variant="inline"
                  name="deadline"
                  autoOk
                  inputVariant="outlined"
                  showTodayButton
                  todayLabel="امروز"
                  okLabel="تایید"
                  cancelLabel="لغو"
                  clearLabel="پاک کردن"
                  labelFunc={(date) => (date ? date.format('jYYYY/jMM/jDD') : '')}
                  value={methods.watch('deadline')}
                  onChange={(e) => methods.setValue('deadline', e)}
                  minDate={new Date()}
                />
              </DatePickerPod>
            </Grid>
            <Grid container>
              <Grid item xs={12} sm={6} md={6} lg={4}>
                <AutocompleteV
                  placeholder="جستجو و انتخاب کنید..."
                  control={methods.control}
                  errors={methods.formState.errors}
                  register={methods.register}
                  name="guildCode"
                  label="محل واریز درآمد"
                  list={businessGuildsList}
                  preventAddCustomOption
                  disableClearable
                  rules={['required']}
                  loading={guildListLoading}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <CustomTable
                methods={methods}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
              />
            </Grid>
            <Grid container style={{ marginInlineStart: 8 }}>
              <Grid item lg={4} md={6} xs={12}>
                <InputV2
                  fullWidth
                  errors={methods.formState.errors}
                  label="توضیحات"
                  name="description"
                  register={methods.register}
                  control={methods.control}
                  defaultValue=""
                />
              </Grid>
            </Grid>
            <Grid container style={{ marginInlineStart: 8 }}>
              <Grid item lg={4} md={6} xs={12}>
                <InputV2
                  fullWidth
                  errors={methods.formState.errors}
                  rules={['required']}
                  currency
                  label="مالیات"
                  minimumValue="0"
                  maximumValue="100"
                  currencyText="درصد"
                  name="preferredTaxRate"
                  register={methods.register}
                  control={methods.control}
                  defaultValue="9"
                />
              </Grid>
            </Grid>
            <Grid container style={{ marginInlineStart: 8 }}>
              <Grid item lg={4} md={6} xs={12}>
                <InputV2
                  placeholder="می‌توانید چندین بن را با کاراکتر ',' از هم جدا کنید"
                  fullWidth
                  dir="ltr"
                  name="voucher_temp"
                  label="بن تخفیف"
                  tooltip="بن تخفیف‌هایی که می‌توانند برای پرداخت این فاکتور استفاده شوند را وارد کنید یا فایل txt حاوی کدها را وارد کنید."
                  value={voucherHash}
                  forceValue={voucherHash}
                  englishNumber
                  onChangeValue={(ev) => {
                    setVoucherHash(ev?.target?.value);
                  }}
                />
                <div style={{ marginBottom: 10 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!voucherHash}
                    onClick={() => onAddVoucher()}
                  >
                    افزودن
                  </Button>
                  <input
                    key={fileCounter}
                    hidden
                    accept="text/plain"
                    id="raised-button-file-voucher"
                    type="file"
                    multiple={false}
                    onChange={onSelectFile}
                    ref={inputRef}
                  />
                  <Button
                    startIcon={<Icons icon="Document" color="#2b3990" size="23" />}
                    variant="contained"
                    onClick={() => inputRef.current?.click?.()}
                    style={{ marginInlineStart: 10, padding: 15 }}
                  >
                    بارگزاری فایل .txt
                  </Button>
                </div>
                <PickList
                  ltr
                  items={voucherHashs}
                  onRemove={onDeleteVoucher}
                  title="بن تخفیف"
                  style={{ marginTop: 8, marginBottom: 8 }}
                  titleProps={{ style: { fontFamily: 'Vazir' } }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <RadioGroupPod
                label="مشتری می تواند بعدا کد تخفیف روی فاکتور اعمال کند"
                control={methods.control}
                name="postVoucherEnabled"
                options={[
                  { value: 'true', label: 'بله', placement: 'end' },
                  { value: 'false', label: 'خیر', placement: 'end' },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <RadioGroupPod
                label="پرداخت فاکتور توسط مشتری نیازمند تایید شما نیز می باشد"
                control={methods.control}
                name="verificationNeeded"
                options={[
                  { value: 'true', label: 'بله', placement: 'end' },
                  { value: 'false', label: 'خیر', placement: 'end' },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} xs={12} style={{ marginTop: 8 }} justifyContent="flex-end">
            <ButtonLoading
              variant="contained"
              color="primary"
              loading={previewLoading}
              name="صدور فاکتور"
              type="submit"
            />
          </Grid>
        </form>
      </FormProvider>
      <ConfirmDialog
        open={isOpenPreview}
        onClose={() => setIsOpenPreview(false)}
        onConfirm={issueInvoice}
        title="پیش نمایش"
        confirmButtonText="صدور"
        cancelButtonProps="انصراف"
        loading={loading}
      >
        {currencyText && <Preview data={previewData} currencyText={currencyText} />}
      </ConfirmDialog>
    </PageContainer>
  );
};

export default ImprestIssueInvoice;
